import { useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { AlertTriangle, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { api, apiError, fmtHour } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";

const PAID_ACTIVE = (x) => x.payment_status === "paid" && !["cancelled", "slot_conflict"].includes(x.status);

const hourNote = (h, day, taken, len) => {
  if (!day) return "";
  const span = Array.from({ length: len }, (_, i) => h + i);
  if (span.some((x) => taken.has(x))) return "booked";
  if (span.some((x) => day.blocked_hours.includes(x))) return "blocked";
  if (!day.open || h < day.start || h + len > day.end) return "closed";
  return "";
};

export const RescheduleDialog = ({ booking, onClose, onDone }) => {
  const [date, setDate] = useState("");
  const [start, setStart] = useState(10);
  const [hours, setHours] = useState(1);
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(true);
  const [day, setDay] = useState(null);
  const [conflicts, setConflicts] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!booking) return;
    setDate(booking.event_date); setStart(booking.start_time); setHours(booking.hours || 1);
    setNote(""); setNotify(true); setConflicts(null); setBusy(false);
  }, [booking]);
  useEffect(() => {
    if (!booking || !date) return;
    setDay(null); setConflicts(null);
    api.get("/availability/day", { params: { date, service_id: booking.service_id } }).then((r) => setDay(r.data)).catch(() => setDay(null));
  }, [booking, date]);
  const qc = useQueryClient();
  const notes = useMemo(() => {
    const taken = new Set();
    (qc.getQueryData(["admin-bookings"]) || []).filter((x) => x.id !== booking?.id && x.event_date === date && PAID_ACTIVE(x))
      .forEach((x) => { for (let k = 0; k < (x.hours || 1); k++) taken.add(x.start_time + k); });
    return Array.from({ length: 24 }, (_, h) => hourNote(h, day, taken, hours));
  }, [qc, day, booking, date, hours]);

  const save = async (force) => {
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/reschedule`, { event_date: date, start_time: Number(start), hours: Number(hours), notify, note: notify ? note.trim() : "", force });
      toast.success(notify ? "Rescheduled — client notified" : "Rescheduled");
      onDone(); onClose();
    } catch (e) {
      const c = e.response?.status === 409 && e.response.data?.detail?.conflicts;
      if (c) setConflicts(c); else toast.error(apiError(e));
    } finally { setBusy(false); }
  };
  const req = booking?.change_request && !booking.change_request.resolved ? booking.change_request : null;
  const pick = (fn) => (v) => { fn(v); setConflicts(null); };

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto" data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Reschedule booking</DialogTitle>
          <DialogDescription className="text-white/50">
            {booking && `${booking.name} — ${booking.service_title}. Now: ${format(parseISO(booking.event_date), "EEE, MMM d, yyyy")} at ${fmtHour(booking.start_time)} (${booking.hours}h)`}
          </DialogDescription>
        </DialogHeader>
        {req && (
          <p className="text-amber-300 text-xs bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 flex gap-2" data-testid="reschedule-client-request">
            <MessageSquare size={14} className="shrink-0 mt-0.5" /> Client asked to {req.type}: {req.message || "(no message)"}
          </p>
        )}
        <div className="space-y-4">
          <div>
            <label className="text-white/50 text-[10px] uppercase font-mono-label">New date</label>
            <Input type="date" value={date} onChange={(e) => pick(setDate)(e.target.value)} className="mt-1 bg-[#0a0a0a] border-white/15 [color-scheme:dark]" data-testid="reschedule-date" />
            {date && date < format(new Date(), "yyyy-MM-dd") && <p className="text-amber-400 text-xs mt-1" data-testid="reschedule-past-warning">This date is in the past.</p>}
            {day && (!day.open || day.blocked) && <p className="text-amber-400 text-xs mt-1">{day.blocked ? `Blocked${day.blocked_reason ? `: ${day.blocked_reason}` : ""}` : "Studio closed this day"}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/50 text-[10px] uppercase font-mono-label">Start time</label>
              <Select value={String(start)} onValueChange={pick((v) => setStart(Number(v)))}>
                <SelectTrigger className="mt-1 bg-[#0a0a0a] border-white/15" data-testid="reschedule-start"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/15 text-white max-h-72">
                  {Array.from({ length: 25 - hours }, (_, h) => (
                    <SelectItem key={h} value={String(h)} data-testid={`reschedule-start-${h}`}>
                      {fmtHour(h)}{notes[h] && <span className="text-amber-400/80 text-[10px] ml-2 uppercase">{notes[h]}</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/50 text-[10px] uppercase font-mono-label">Length</label>
              <Select value={String(hours)} onValueChange={pick((v) => { setHours(Number(v)); if (start + Number(v) > 24) setStart(24 - Number(v)); })}>
                <SelectTrigger className="mt-1 bg-[#0a0a0a] border-white/15" data-testid="reschedule-hours"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/15 text-white max-h-72">
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => <SelectItem key={h} value={String(h)}>{h} hour{h > 1 ? "s" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {hours !== booking?.hours && <p className="text-white/40 text-[11px]">Changing the length doesn't change what the client paid.</p>}
          <label className="flex items-center justify-between gap-3 rounded-lg border border-white/10 px-3 py-2.5">
            <span className="text-sm text-white/80">Notify client <span className="text-white/40 text-xs block">Email, plus a text if they opted in to SMS</span></span>
            <Switch checked={notify} onCheckedChange={setNotify} data-testid="reschedule-notify-switch" />
          </label>
          <div>
            <label className="text-white/50 text-[10px] uppercase font-mono-label">Note to client (optional)</label>
            <Textarea rows={2} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} disabled={!notify}
              placeholder="e.g. Moved as you requested — see you then!" className="mt-1 bg-[#0a0a0a] border-white/15 text-sm" data-testid="reschedule-note" />
          </div>
          {conflicts && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5" data-testid="reschedule-conflicts">
              <p className="text-amber-300 text-xs font-semibold flex items-center gap-1.5 mb-1"><AlertTriangle size={13} /> This time isn't open</p>
              <ul className="text-amber-200/80 text-xs list-disc pl-5 space-y-0.5">{conflicts.map((c) => <li key={c}>{c}</li>)}</ul>
            </div>
          )}
          <div className="flex gap-2 justify-end flex-wrap">
            <button onClick={onClose} className="rounded-full px-5 py-2 text-xs border border-white/15 text-white/70" data-testid="reschedule-cancel-btn">Close</button>
            {conflicts ? (
              <button onClick={() => save(true)} disabled={busy} className="rounded-full px-5 py-2 text-xs bg-amber-500 text-black font-semibold disabled:opacity-50" data-testid="reschedule-force-btn">
                {busy ? "Saving…" : "Reschedule anyway"}
              </button>
            ) : (
              <button onClick={() => save(false)} disabled={busy || !date} className="btn-gold rounded-full px-5 py-2 text-xs font-semibold disabled:opacity-50" data-testid="reschedule-save-btn">
                {busy ? "Saving…" : "Reschedule"}
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
